import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { SERVICES } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", service: "", preference: "", message: "" };

const fieldCls =
  "bg-[#121413] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-lime-400 h-12";

export const BookingForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target ? e.target.value : e });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, preference: form.preference || "No preference" });
      setSubmitted(true);
      setForm(EMPTY);
      toast.success("Booking enquiry sent — we'll be in touch within one working day.");
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="border border-lime-400/30 bg-lime-400/5 rounded-2xl p-10 text-center" data-testid="booking-success-message">
        <CheckCircle2 className="mx-auto text-lime-400 mb-4" size={40} />
        <h3 className="font-display text-2xl text-zinc-100 mb-2">Enquiry received</h3>
        <p className="text-zinc-400 text-sm max-w-md mx-auto">
          Thanks for getting in touch — we've got your details and will reply within one working day.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="mt-6 text-sm text-lime-300 underline underline-offset-4 hover:text-lime-200 transition-colors duration-300"
          data-testid="booking-send-another-button"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-5" data-testid="booking-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Input required minLength={2} placeholder="Your name" value={form.name} onChange={set("name")} className={fieldCls} data-testid="booking-name-input" />
        <Input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={fieldCls} data-testid="booking-email-input" />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Input required minLength={5} placeholder="Phone number" value={form.phone} onChange={set("phone")} className={fieldCls} data-testid="booking-phone-input" />
        <Select required value={form.service} onValueChange={set("service")}>
          <SelectTrigger className={fieldCls} data-testid="booking-service-select">
            <SelectValue placeholder="Which service?" />
          </SelectTrigger>
          <SelectContent className="bg-[#121413] border-white/10 text-zinc-100">
            {SERVICES.map((s) => (
              <SelectItem key={s.id} value={s.title} data-testid={`booking-service-option-${s.id}`}>
                {s.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Select value={form.preference} onValueChange={set("preference")}>
          <SelectTrigger className={fieldCls} data-testid="booking-preference-select">
            <SelectValue placeholder="Who would you like to hear from? (optional)" />
          </SelectTrigger>
          <SelectContent className="bg-[#121413] border-white/10 text-zinc-100">
            <SelectItem value="No preference" data-testid="booking-preference-option-none">No preference</SelectItem>
            <SelectItem value="Lewis" data-testid="booking-preference-option-lewis">Lewis</SelectItem>
            <SelectItem value="Emily" data-testid="booking-preference-option-emily">Emily</SelectItem>
          </SelectContent>
        </Select>
        <p className="mt-2 text-xs text-zinc-500">
          As a husband &amp; wife team, we're happy to send whoever you're most comfortable with.
        </p>
      </div>
      <Textarea
        required
        minLength={5}
        rows={5}
        placeholder="Tell us about the job — what needs doing, where, and when suits you."
        value={form.message}
        onChange={set("message")}
        className="bg-[#121413] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-lime-400"
        data-testid="booking-message-input"
      />
      <Button
        type="submit"
        disabled={loading}
        className="rounded-full bg-lime-400 text-black font-semibold h-12 px-10 w-fit transition-all duration-300 hover:bg-lime-300 hover:-translate-y-0.5 disabled:opacity-60"
        data-testid="booking-submit-button"
      >
        {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
        {loading ? "Sending…" : "Send booking enquiry"}
      </Button>
    </form>
  );
};
